import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { WorkflowCanvas } from './WorkflowCanvas';
import { NodeTemplate, WorkflowCanvasState, WorkflowConnection, WorkflowNode as WorkflowNodeType } from '../../types/workflow';
import { ConversationNodeClass } from '../../types/workflow-spec';
import { autoArrangeWorkflow } from '../../lib/workflow/autoArrangeWorkflow';
import { WorkflowInspector } from './WorkflowInspector';
import Modal from '../ui/Modal';
import { AgentConfigPanel } from '../AgentConfig/AgentConfigPanel';
import { workflowService, type WorkflowAgentInstance } from '../../services/workflowService';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { TextField } from '../ui/Field';
import { Surface } from '../ui/Surface';
import { ChatbotConfigView } from '../../pages/ChatbotConfig';

type WorkflowPublishStatus = 'draft' | 'published' | 'archived' | 'paused';

/** Set when the workflow was provisioned by an AI Pod subscription. */
export type WorkflowPodLink = {
  aiPodId: string;
  podName: string;
  channel: 'chat' | 'workflow' | 'both';
  chatbotId: string | null;
  status: string;
};


interface WorkflowBuilderProps {
  workflowId?: string;
  initialName?: string;
  initialStatus?: WorkflowPublishStatus;
  initialNodes?: WorkflowNodeType[];
  initialConnections?: WorkflowConnection[];
  onSave?: (workflow: {
    id?: string;
    name: string;
    status: WorkflowPublishStatus;
    nodes: WorkflowNodeType[];
    connections: WorkflowConnection[];
  }) => void;
  onRun?: (workflow: { name: string; nodes: WorkflowNodeType[]; connections: WorkflowConnection[] }) => void;
  onTest?: () => void;
  onConversations?: () => void;
  onBack?: () => void;
  /** A pod workflow keeps the pod's steps and agents: no palette, chatbot available here. */
  podSubscription?: WorkflowPodLink | null;
}

const WORKFLOW_BLOCK_TEMPLATES: NodeTemplate[] = [
  {
    type: 'trigger',
    name: 'Trigger',
    description: 'Start a workflow from an inbound event or manual run',
    icon: '◉',
    category: 'triggers',
    color: '#16a34a',
    inputs: [],
    outputs: [{ id: 'out', name: 'start', type: 'object' }],
    defaultData: { nodeClass: 'trigger', triggerType: 'manual' },
  },
  {
    type: 'condition',
    name: 'Router',
    description: 'Branch the conversation based on intent, outcome, or custom logic',
    icon: '◇',
    category: 'logic',
    color: '#f59e0b',
    inputs: [{ id: 'in', name: 'input', type: 'object' }],
    outputs: [
      { id: 'true', name: 'true', type: 'object' },
      { id: 'false', name: 'false', type: 'object' },
    ],
    defaultData: { nodeClass: 'router', expression: 'payload.flag === true' },
  },
  {
    type: 'api-call',
    name: 'Action',
    description: 'Run a system action or backend task inside the workflow',
    icon: '□',
    category: 'communication',
    color: '#2563eb',
    inputs: [{ id: 'in', name: 'input', type: 'object' }],
    outputs: [{ id: 'out', name: 'result', type: 'object' }],
    defaultData: { nodeClass: 'action' },
  },
  {
    type: 'webhook',
    name: 'Human Handoff',
    description: 'Escalate the conversation to a human queue or support team',
    icon: '↗',
    category: 'communication',
    color: '#ea580c',
    inputs: [{ id: 'in', name: 'input', type: 'object' }],
    outputs: [{ id: 'out', name: 'handoff', type: 'object' }],
    defaultData: { nodeClass: 'human-handoff' },
  },
  {
    type: 'delay',
    name: 'End',
    description: 'Terminate the workflow with a success or failure outcome',
    icon: '◎',
    category: 'utilities',
    color: '#ef4444',
    inputs: [{ id: 'in', name: 'end', type: 'object' }],
    outputs: [],
    defaultData: { nodeClass: 'end', result: 'success' },
  },
];

const buildAgentTemplates = (agents: WorkflowAgentInstance[]): NodeTemplate[] =>
  agents.map((agent) => ({
    type: 'ai-agent',
    name: agent.name,
    description: agent.description ?? agent.agentType?.name ?? 'Functional agent',
    icon: '🤖',
    category: 'ai',
    color: '#3b82f6',
    inputs: [{ id: 'in', name: 'input', type: 'object' }],
    outputs: [{ id: 'out', name: 'output', type: 'object' }],
    defaultData: {
      nodeKind: 'agent',
      nodeClass: 'conversation',
      agentId: agent.id,
      agentVersion: agent.version,
    },
  }));

const GRID_SIZE = 24;

const snapToGrid = (position: { x: number; y: number }) => ({
  x: Math.round(position.x / GRID_SIZE) * GRID_SIZE,
  y: Math.round(position.y / GRID_SIZE) * GRID_SIZE,
});

export const WorkflowBuilder: React.FC<WorkflowBuilderProps> = ({
  workflowId,
  initialName = 'Untitled Workflow',
  initialStatus = 'draft',
  initialNodes = [],
  initialConnections = [],
  onSave,
  onRun,
  onTest,
  onConversations,
  podSubscription,
  onBack,
}) => {
  const [name, setName] = useState(initialName);
  const [publishStatus, setPublishStatus] = useState<WorkflowPublishStatus>(initialStatus);

  useEffect(() => {
    setPublishStatus(initialStatus);
  }, [initialStatus]);
  const [entryAgents, setEntryAgents] = useState<WorkflowAgentInstance[]>([]);
  const [functionalAgents, setFunctionalAgents] = useState<WorkflowAgentInstance[]>([]);
  const [agentConfigModal, setAgentConfigModal] = useState<{ open: boolean; agentId: string | null }>({ open: false, agentId: null });
  const [chatConfigOpen, setChatConfigOpen] = useState(false);
  const podPaused = !!podSubscription && podSubscription.status !== 'active' && podSubscription.status !== 'trialing';
  const [canvasState, setCanvasState] = useState<WorkflowCanvasState>({
    nodes: initialNodes,
    connections: initialConnections,
    isConnecting: false,
  });

  useEffect(() => {
    const loadAgents = async () => {
      try {
        const [entry, functional] = await Promise.all([
          workflowService.listEntryAgents(undefined, workflowId),
          workflowService.listFunctionalAgents(workflowId),
        ]);
        setEntryAgents(entry);
        setFunctionalAgents(functional);
      } catch (e: any) {
        toast.error(e?.message || 'Failed to load workflow agents');
      }
    };
    loadAgents();
  }, [workflowId]);

  const agentTemplates = useMemo(() => buildAgentTemplates(functionalAgents), [functionalAgents]);

  const handleUpdateCanvasState = useCallback((updates: Partial<WorkflowCanvasState>) => {
    setCanvasState((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleAddNode = useCallback((template: NodeTemplate, position?: { x: number; y: number }) => {
    const fallbackPosition = {
      x: 72 + (canvasState.nodes.length % 3) * 264,
      y: 72 + Math.floor(canvasState.nodes.length / 3) * 168,
    };
    const newNode: WorkflowNodeType = {
      id: `node-${Date.now()}`,
      type: template.type,
      name: template.name,
      title: template.name,
      summary: template.description,
      nodeClass: (template.defaultData.nodeClass as ConversationNodeClass | undefined) ?? undefined,
      position: snapToGrid(position ?? fallbackPosition),
      data: { ...template.defaultData },
      inputs: template.inputs,
      outputs: template.outputs,
      status: 'idle',
    };
    setCanvasState((prev) => ({ ...prev, nodes: [...prev.nodes, newNode] }));
  }, [canvasState.nodes.length]);

  const handleUpdateNode = useCallback((nodeId: string, updates: Partial<WorkflowNodeType>) => {
    setCanvasState((prev) => {
      const existingNode = prev.nodes.find((node) => node.id === nodeId);
      if (!existingNode) return prev;

      const mergedData = updates.data ? { ...existingNode.data, ...updates.data } : existingNode.data;
      const nextNodeClass =
        (updates.nodeClass as ConversationNodeClass | undefined) ??
        (mergedData.nodeClass as ConversationNodeClass | undefined) ??
        existingNode.nodeClass;

      const nextNode = {
        ...existingNode,
        ...updates,
        data: mergedData,
        nodeClass: nextNodeClass,
        ...(nextNodeClass ? { data: { ...mergedData, nodeClass: nextNodeClass } } : {}),
      };
      const nextNodes = prev.nodes.map((node) => (node.id === nodeId ? nextNode : node));

      const allowedInputIds = new Set(nextNode.inputs.map((input) => input.id));
      const allowedOutputIds = new Set(nextNode.outputs.map((output) => output.id));
      const nextConnections = prev.connections.filter((connection) => {
        if (connection.sourceNodeId === nodeId && !allowedOutputIds.has(connection.sourceOutputId)) {
          return false;
        }
        if (connection.targetNodeId === nodeId && !allowedInputIds.has(connection.targetInputId)) {
          return false;
        }
        return true;
      });

      return {
        ...prev,
        nodes: nextNodes,
        connections: nextConnections,
      };
    });
  }, []);

  const handleDeleteNode = useCallback((nodeId: string) => {
    // An AI Pod workflow keeps the pod's steps.
    if (podSubscription) {
      toast.info("Steps can't be removed from an AI Pod workflow.");
      return;
    }
    setCanvasState((prev) => ({
      ...prev,
      nodes: prev.nodes.filter((n) => n.id !== nodeId),
      connections: prev.connections.filter((c) => c.sourceNodeId !== nodeId && c.targetNodeId !== nodeId),
    }));
  }, [podSubscription]);

  const handleAddConnection = useCallback((connection: WorkflowConnection) => {
    setCanvasState((prev) => ({ ...prev, connections: [...prev.connections, connection] }));
  }, []);

  const handleDeleteConnection = useCallback((connectionId: string) => {
    setCanvasState((prev) => ({
      ...prev,
      connections: prev.connections.filter((c) => c.id !== connectionId),
      selectedConnectionId: prev.selectedConnectionId === connectionId ? undefined : prev.selectedConnectionId,
    }));
  }, []);

  const handleUpdateConnection = useCallback((connectionId: string, updates: Partial<WorkflowConnection>) => {
    setCanvasState((prev) => ({
      ...prev,
      connections: prev.connections.map((connection) =>
        connection.id === connectionId ? { ...connection, ...updates } : connection
      ),
    }));
  }, []);

  const handleAddRouterBranch = useCallback((nodeId: string) => {
    setCanvasState((prev) => ({
      ...prev,
      nodes: prev.nodes.map((node) => {
        if (node.id !== nodeId) return node;
        const branchNumber = node.outputs.length + 1;
        return {
          ...node,
          outputs: [
            ...node.outputs,
            {
              id: `branch-${Date.now()}-${branchNumber}`,
              name: `Branch ${branchNumber}`,
              type: 'object',
            },
          ],
        };
      }),
    }));
  }, []);

  const handleAutoArrange = useCallback(() => {
    setCanvasState((prev) => ({
      ...prev,
      nodes: autoArrangeWorkflow(prev.nodes, prev.connections),
    }));
  }, []);

  const onDragStartTemplate = useCallback(
    (template: NodeTemplate) => (e: React.DragEvent) => {
      const payload = JSON.stringify(template);
      try {
        e.dataTransfer.setData('application/json', payload);
      } catch {}
      try {
        e.dataTransfer.setData('text/plain', payload);
      } catch {}
      e.dataTransfer.effectAllowed = 'copy';
    },
    []
  );

  const hasOutboundCallTrigger = useMemo(
    () =>
      canvasState.nodes.some(
        (node) =>
          (node.nodeClass === 'trigger' || node.data?.nodeClass === 'trigger') &&
          node.data?.triggerType === 'outbound-call'
      ),
    [canvasState.nodes]
  );

  const workflowPayload = useMemo(
    () => ({
      id: workflowId,
      name,
      status: publishStatus,
      nodes: canvasState.nodes,
      connections: canvasState.connections,
    }),
    [workflowId, name, publishStatus, canvasState.nodes, canvasState.connections]
  );

  const selectedNode = useMemo(
    () => canvasState.nodes.find((node) => node.id === canvasState.selectedNodeId),
    [canvasState.nodes, canvasState.selectedNodeId]
  );

  const selectedConnection = useMemo(
    () => canvasState.connections.find((connection) => connection.id === canvasState.selectedConnectionId),
    [canvasState.connections, canvasState.selectedConnectionId]
  );

  // Unpublished-changes indicator. Starts clean on load (fresh session, nothing
  // to publish yet); any content edit (nodes/connections/name — NOT mere
  // selection) marks it dirty. Save draft persists the work but does NOT publish,
  // so the strip STAYS; only Publish makes the changes live and clears it.
  const [hasUnpublishedChanges, setHasUnpublishedChanges] = useState(false);
  const skipFirstDirtyRef = useRef(true);
  useEffect(() => {
    if (skipFirstDirtyRef.current) { skipFirstDirtyRef.current = false; return; }
    setHasUnpublishedChanges(true);
  }, [canvasState.nodes, canvasState.connections, name]);

  const handleSaveDraft = useCallback(() => {
    // Saves as a draft but the changes are still not live — strip stays until Publish.
    onSave?.({ ...workflowPayload, status: 'draft' });
  }, [onSave, workflowPayload]);

  const handlePublish = useCallback(() => {
    setPublishStatus('published');
    onSave?.({ ...workflowPayload, status: 'published' });
    setHasUnpublishedChanges(false);
  }, [onSave, workflowPayload]);

  return (
    <div className="flex min-h-[calc(100dvh-15rem)] min-w-0 flex-col gap-4 xl:flex-row">
      {/* No palette for an AI Pod workflow: its steps and agents come from the pod. */}
      {!podSubscription && (
      <aside className="flex min-w-0 flex-col rounded-xl border border-gray-200 bg-white p-4 shadow-sm xl:w-72 xl:shrink-0">
        <div className="flex items-center justify-between border-b border-gray-100 pb-3">
          <h2 className="text-lg font-semibold text-gray-900">Palette</h2>
          <span className="text-xs text-gray-500">{WORKFLOW_BLOCK_TEMPLATES.length + agentTemplates.length}</span>
        </div>

        <div className="mt-3 space-y-5 overflow-y-auto pr-1 max-h-[24rem] xl:max-h-[calc(100dvh-20rem)]">
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">Workflow Blocks</h3>
              <span className="text-[10px] text-gray-400">{WORKFLOW_BLOCK_TEMPLATES.length}</span>
            </div>
            <div className="space-y-3">
              {WORKFLOW_BLOCK_TEMPLATES.map((tpl) => (
                <div
                  key={tpl.name}
                  className="cursor-grab rounded-lg border border-gray-200 p-3 transition-colors hover:border-blue-400 hover:bg-blue-50/30 active:cursor-grabbing"
                  draggable
                  onDragStart={onDragStartTemplate(tpl)}
                  title={tpl.description}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="truncate text-sm font-medium text-gray-900">
                      {tpl.icon} {tpl.name}
                    </div>
                    <Badge className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] text-gray-700">{tpl.category}</Badge>
                  </div>
                  <div className="mt-1 break-words text-xs text-gray-500">{tpl.description}</div>
                </div>
              ))}
            </div>
          </section>

          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">Agents</h3>
              <span className="text-[10px] text-gray-400">{agentTemplates.length}</span>
            </div>
            <p className="text-xs leading-5 text-gray-500">
              Drag existing agents directly into the canvas and wire them with workflow blocks.
            </p>
            <div className="space-y-3">
              {agentTemplates.map((tpl) => (
                <div
                  key={tpl.name}
                  className="cursor-grab rounded-lg border border-blue-100 bg-blue-50/40 p-3 transition-colors hover:border-blue-400 hover:bg-blue-50 active:cursor-grabbing"
                  draggable
                  onDragStart={onDragStartTemplate(tpl)}
                  title={tpl.description}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="truncate text-sm font-medium text-gray-900">
                      {tpl.icon} {tpl.name}
                    </div>
                    <Badge className="shrink-0 rounded-full bg-white px-2 py-0.5 text-[10px] text-blue-700">{tpl.category}</Badge>
                  </div>
                  <div className="mt-1 break-words text-xs text-gray-500">{tpl.description}</div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </aside>
      )}

      <div className="flex min-w-0 flex-1 flex-col gap-3">
        {podSubscription ? (
          <div
            className={`rounded-xl border px-4 py-2.5 text-sm ${
              podPaused ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-blue-200 bg-blue-50 text-blue-800'
            }`}
          >
            {podPaused
              ? `This workflow is paused because your ${podSubscription.podName} subscription isn't active.`
              : `Part of your ${podSubscription.podName} subscription. You can configure each step and its agent; steps and agents can't be added or removed.`}
          </div>
        ) : null}
        {hasUnpublishedChanges ? (
          <div className="relative z-[45] flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
            <span className="font-medium">You have unpublished changes — publish to make them live.</span>
            <Button tone="custom"
              type="button"
              onClick={handlePublish}
              className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700"
            >
              Publish now
            </Button>
          </div>
        ) : null}
        <Surface tone="card-soft" className="flex flex-wrap items-center gap-3 rounded-xl border p-4">
          {onBack ? (
            <Button tone="outline-strong"
              className="rounded-md border px-3 py-2"
              onClick={onBack}
              type="button"
            >
              Back
            </Button>
          ) : null}
          <TextField
            className="min-w-0 flex-1 rounded-md border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Workflow name"
          />
          {podSubscription?.chatbotId ? (
            <Button tone="outline-strong" className="rounded-md border px-3 py-2" type="button" onClick={() => setChatConfigOpen(true)}>
              Configure chat
            </Button>
          ) : null}
          {hasOutboundCallTrigger ? (
            <span
              className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                publishStatus === 'published'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-amber-100 text-amber-800'
              }`}
              title={
                publishStatus === 'published'
                  ? 'HubSpot leads will be dialed by the outbound cron'
                  : 'Draft workflows are not dialed — publish to enable calls'
              }
            >
              {publishStatus === 'published' ? 'Published · calls active' : 'Draft · calls off'}
            </span>
          ) : null}
          <Button tone="outline-strong"
            className="rounded-md border px-3 py-2"
            onClick={handleSaveDraft}
            type="button"
          >
            Save draft
          </Button>
          <Button tone="blue"
            className="rounded-md px-3 py-2"
            onClick={handlePublish}
            type="button"
          >
            Publish
          </Button>
          <Button tone="outline-strong"
            className="rounded-md border px-3 py-2"
            onClick={handleAutoArrange}
          >
            Auto Arrange
          </Button>
          <Button tone="custom"
            className="rounded-md bg-green-600 px-3 py-2 text-white hover:bg-green-700"
            onClick={() => onRun?.(workflowPayload)}
          >
            Run
          </Button>
          {onTest ? (
            <Button tone="custom"
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-800 hover:bg-gray-50"
              onClick={() => onTest()}
              type="button"
            >
              Test
            </Button>
          ) : null}
          {onConversations ? (
            <Button tone="custom"
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-800 hover:bg-gray-50"
              onClick={() => onConversations()}
              type="button"
            >
              Conversations
            </Button>
          ) : null}
        </Surface>

        <div className="flex min-w-0 flex-1 gap-4 overflow-hidden">
          <div className="min-w-0 flex-1 overflow-hidden">
            <WorkflowCanvas
              canvasState={canvasState}
              onUpdateCanvasState={handleUpdateCanvasState}
              onUpdateNode={handleUpdateNode}
              onDeleteNode={handleDeleteNode}
              onAddConnection={handleAddConnection}
              onDeleteConnection={handleDeleteConnection}
              onAddNode={handleAddNode}
              onAddRouterBranch={handleAddRouterBranch}
              onOpenAgentConfig={(agentId) => setAgentConfigModal({ open: true, agentId })}
            />
          </div>
          <WorkflowInspector
            workflowId={workflowId}
            selectedNode={selectedNode}
            selectedConnection={selectedConnection}
            entryAgents={entryAgents}
            functionalAgents={functionalAgents}
            onUpdateNode={handleUpdateNode}
            onDeleteNode={handleDeleteNode}
            onUpdateConnection={handleUpdateConnection}
            onDeleteConnection={handleDeleteConnection}
            onAddRouterBranch={handleAddRouterBranch}
            onClose={() => handleUpdateCanvasState({ selectedNodeId: undefined, selectedConnectionId: undefined })}
            onSaveDraft={handleSaveDraft}
          />
        </div>
      </div>

      <Modal
        open={agentConfigModal.open}
        onClose={() => setAgentConfigModal({ open: false, agentId: null })}
        size="xl"
        showCloseButton
        closeOnBackdropClick
        className="!max-w-[92rem] !max-h-[94dvh] !bg-[linear-gradient(90deg,#FFFFFF_0%,rgba(237,101,113,0.10)_100%)]"
        headerClassName="border-0"
        bodyClassName="p-0 min-h-[80vh]"
      >
        {agentConfigModal.agentId && (
          <AgentConfigPanel
            agentId={agentConfigModal.agentId}
            onDelete={() => setAgentConfigModal({ open: false, agentId: null })}
          />
        )}
      </Modal>

      {podSubscription?.chatbotId ? (
        <Modal isOpen={chatConfigOpen} onClose={() => setChatConfigOpen(false)} title={`${podSubscription.podName} — chat`} size="full">
          <ChatbotConfigView chatbotId={podSubscription.chatbotId} embedded />
        </Modal>
      ) : null}
    </div>
  );
};

export default WorkflowBuilder;
